"use client";

// CommandPanelHost mounts the active command panel inside the app
// tree. Handlers can't render React themselves, so the invoker
// fires a CustomEvent that this host listens for. Only one panel
// is shown at a time — opening a second event closes the first.
import * as React from "react";
import { COMMAND_PANEL_EVENT, type OpenPanelDetail } from "@/lib/commands/handler";
import type { PanelComponent } from "@/lib/commands/types";
import { useCommandContext } from "@/lib/commands/context";

export function CommandPanelHost() {
  const [active, setActive] = React.useState<PanelComponent | null>(null);
  // Stash the context snapshot at open time. If the user navigates
  // while the panel is open, the panel still sees the entity it
  // was opened for.
  const [ctxSnapshot, setCtxSnapshot] = React.useState<OpenPanelDetail["ctxSnapshot"] | null>(
    null,
  );
  const ctx = useCommandContext();

  React.useEffect(() => {
    function onOpen(e: Event) {
      const detail = (e as CustomEvent<OpenPanelDetail>).detail;
      setActive(() => detail.panel);
      setCtxSnapshot(detail.ctxSnapshot);
    }
    window.addEventListener(COMMAND_PANEL_EVENT, onOpen);
    return () => window.removeEventListener(COMMAND_PANEL_EVENT, onOpen);
  }, []);

  if (!active || !ctxSnapshot) return null;
  const Panel = active;
  return (
    <Panel
      ctx={ctxSnapshot ?? ctx}
      onClose={() => {
        setActive(null);
        setCtxSnapshot(null);
      }}
    />
  );
}
